import { Component, ElementRef, ViewChild, OnDestroy, inject, NgZone, afterNextRender } from '@angular/core';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader.js';
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry.js';
import { InstancedFlow } from 'three/examples/jsm/modifiers/CurveModifier.js';
import { isAuth } from './auth';
import { WwwwwwComponent } from "./iniciofront";

@Component({
  selector: 'app-www',
  standalone: true,
  imports: [WwwwwwComponent],
  template: `
    <div class="landing-container">
      <canvas #canvasRef></canvas>
      
      <div class="footer-overlay">
        <app-wwwwww></app-wwwwww>
      </div>
    </div>
  `,
  styles: [`
    .landing-container {
      position: relative;
      width: 100%;
      min-height: 100vh;
      overflow-x: hidden;
      background: #fcfcfc;
      font-family: system-ui, -apple-system, sans-serif;
    }
    canvas {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: block;
      z-index: 1;
    }
    .footer-overlay {
      position: absolute;
      top: 55.5%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 100%;
      z-index: 10;
      pointer-events: auto;
    }
  `]
})
export class Wwww implements OnDestroy {
  @ViewChild('canvasRef', { static: true }) canvasRef!: ElementRef<HTMLCanvasElement>;
  auth = isAuth();

  private renderer!: THREE.WebGLRenderer;
  private scene!: THREE.Scene;
  private camera!: THREE.PerspectiveCamera;
  private controls!: OrbitControls;
  private loadedModel?: THREE.Object3D;
  private pmremGenerator?: THREE.PMREMGenerator;
  private clock = new THREE.Clock();

  private flow?: InstancedFlow;
  private curveHandles: THREE.Mesh[] = [];
  private animationFrameId = 0;
  private ngZone = inject(NgZone);

  private mouseX = 0;
  private mouseY = 0;
  private targetX = 0;
  private targetY = 0;

  private resizeObserver?: ResizeObserver;
  private boundOnMouseMove = this.onMouseMove.bind(this);

  constructor() {
    afterNextRender(() => {
      this.initScene();
    });
  }

  private initScene() {
    const canvas = this.canvasRef.nativeElement;
    const width = canvas.clientWidth || window.innerWidth;
    const height = canvas.clientHeight || window.innerHeight;

    // Escena y Cámara
    this.scene = new THREE.Scene();
    this.scene.fog = new THREE.FogExp2(0xfcfcfc, 0.15);

    this.camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 1000);
    this.camera.position.set(0, 0, 6);

    // Renderizador optimizado
    this.renderer = new THREE.WebGLRenderer({ 
      canvas, 
      antialias: true, 
      alpha: true,
      powerPreference: 'high-performance' 
    });
    this.renderer.setSize(width, height, false);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.2;

    // OrbitControls
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.05;

    // Entorno de iluminación
    this.pmremGenerator = new THREE.PMREMGenerator(this.renderer);
    this.scene.environment = this.pmremGenerator.fromScene(new RoomEnvironment(), 0.04).texture;

    // Luces
    this.scene.add(new THREE.AmbientLight(0xffffff, 0.5));

    const pointLight = new THREE.PointLight(0x38bdf8, 2.0);
    pointLight.position.set(5, 5, 5);
    this.scene.add(pointLight);

    const pointLight2 = new THREE.PointLight(0x818cf8, 4.0);
    pointLight2.position.set(-5, -5, 5);
    this.scene.add(pointLight2);

    // --- CURVAS Y TEXTO FLUIDO ---
    const boxGeometry = new THREE.BoxGeometry(0.1, 0.1, 0.1);
    const boxMaterial = new THREE.MeshBasicMaterial({ visible: false });

    const curves = [
      [
        { x: 1.3, y: -1.8, z: -1.3 },
        { x: 1.3, y: -1.8, z: 1.3 },
        { x: -1.3, y: -1.8, z: 1.3 },
        { x: -1.3, y: -1.8, z: -1.3 },
      ],
      [
        { x: -1.3, y: 1.2, z: -1.3 },
        { x: -1.3, y: 1.2, z: 1.3 },
        { x: 1.3, y: 1.2, z: 1.3 },
        { x: 1.3, y: 1.2, z: -1.3 },
      ]
    ].map((curvePoints) => {
      const curveVertices = curvePoints.map((handlePos) => {
        const handle = new THREE.Mesh(boxGeometry, boxMaterial);
        handle.position.copy(handlePos);
        this.curveHandles.push(handle);
        this.scene.add(handle);
        return handle.position;
      });

      const curve = new THREE.CatmullRomCurve3(curveVertices);
      curve.curveType = 'centripetal';
      curve.closed = true;

      const points = curve.getPoints(50);
      const line = new THREE.LineLoop(
        new THREE.BufferGeometry().setFromPoints(points),
        new THREE.LineBasicMaterial({ color: 0x38bdf8, transparent: true, opacity: 0.1 })
      );

      this.scene.add(line);
      return { curve, line };
    });

    // Carga de fuente
    const fontLoader = new FontLoader();
    fontLoader.load('./Zapfino_Regular.json', (font) => {
      const textGeometry = new TextGeometry('Escuela Superior de Formación Artística "Felipe Guamán Poma de Ayala"', {
        font: font,
        size: 0.17,
        depth: 0.1,
        curveSegments: 12,
        bevelEnabled: true,
        bevelThickness: 0.01,
        bevelSize: 0.01,
        bevelOffset: 0,
        bevelSegments: 1,
      });

      textGeometry.rotateX(Math.PI);
      textGeometry.center();

      const textMaterial = new THREE.MeshStandardMaterial({
        color: 0xffffff,
        roughness: 0.2,
        metalness: 0.8
      });

      const numberOfInstances = 2;
      this.flow = new InstancedFlow(numberOfInstances, curves.length, textGeometry, textMaterial);

      curves.forEach(({ curve }, i) => {
        this.flow!.updateCurve(i, curve);
        this.scene.add(this.flow!.object3D);
      });

      for (let i = 0; i < numberOfInstances; i++) {
        const curveIndex = i % curves.length;
        this.flow.setCurve(i, curveIndex);
        this.flow.moveIndividualAlongCurve(i, i * (1 / numberOfInstances));
        this.flow.object3D.setColorAt(i, new THREE.Color(i % 2 === 0 ? 0x38bdf8 : 0x818cf8));
      }
    });

    // --- CARGA DEL MODELO GLB ---
    const loader = new GLTFLoader();
    loader.load(
      'new.glb',
      (gltf) => {
        this.loadedModel = gltf.scene;
        this.loadedModel.scale.set(6, 6, 6);
        this.loadedModel.position.set(0, -0.3, 0);

        this.loadedModel.traverse((child) => {
          if ((child as THREE.Mesh).isMesh) {
            const mesh = child as THREE.Mesh;
            mesh.castShadow = true;
            mesh.receiveShadow = true;

            if (mesh.material) {
              const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
              
              materials.forEach((mat: any) => {
                if (mat.isMeshStandardMaterial || mat.isMeshPhysicalMaterial) {
                  mat.transparent = true;

                  if (mat.isMeshPhysicalMaterial) {
                    mat.transmission = 0.8;
                    mat.roughness = 0.15;
                    mat.metalness = 0.1;
                    mat.ior = 1.5;
                  } else {
                    mat.roughness = 0.2;
                    mat.metalness = 0.4;
                  }

                  mat.envMapIntensity = 1.8;
                  if (mat.map) mat.map.colorSpace = THREE.SRGBColorSpace;
                  mat.needsUpdate = true;
                }
              });
            }
          }
        });

        this.scene.add(this.loadedModel);
      },
      undefined,
      (error) => {
        console.error('Error al cargar el modelo GLB:', error);
      }
    );

    // ResizeObserver
    this.resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const width = entry.contentRect.width;
        const height = entry.contentRect.height;
        if (width > 0 && height > 0) {
          this.camera.aspect = width / height;
          this.camera.updateProjectionMatrix();
          this.renderer.setSize(width, height, false);
        }
      }
    });
    this.resizeObserver.observe(canvas);

    // Bucle de renderizado
    this.ngZone.runOutsideAngular(() => {
      window.addEventListener('mousemove', this.boundOnMouseMove, { passive: true });

      const animate = () => {
        this.animationFrameId = requestAnimationFrame(animate);

        const elapsedTime = this.clock.getElapsedTime();

        this.targetX += (this.mouseX - this.targetX) * 0.05;
        this.targetY += (this.mouseY - this.targetY) * 0.05;

        if (this.loadedModel) {
          this.loadedModel.rotation.x = this.targetY * 0.3 + elapsedTime * 0.1;
          this.loadedModel.rotation.y = this.targetX * 0.3 + elapsedTime * 0.2;
        }

        if (this.flow) {
          this.flow.moveAlongCurve(-0.001);
        }

        this.controls.update();
        this.renderer.render(this.scene, this.camera);
      };
      animate();
    });
  }

  private onMouseMove(event: MouseEvent) {
    this.mouseX = (event.clientX / window.innerWidth) * 2 - 1;
    this.mouseY = -(event.clientY / window.innerHeight) * 2 + 1;
  }

  ngOnDestroy() {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    window.removeEventListener('mousemove', this.boundOnMouseMove);

    if (this.controls) {
      this.controls.dispose();
    }

    if (this.pmremGenerator) {
      this.pmremGenerator.dispose();
    }

    if (this.scene) {
      this.scene.traverse((child) => {
        if ((child as THREE.Mesh).isMesh) {
          const mesh = child as THREE.Mesh;
          
          if (mesh.geometry) {
            mesh.geometry.dispose();
          }
          
          if (mesh.material) {
            const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
            materials.forEach((mat: any) => {
              Object.keys(mat).forEach((key) => {
                const value = mat[key];
                if (value && typeof value === 'object' && 'dispose' in value && typeof value.dispose === 'function') {
                  value.dispose();
                }
              });
              mat.dispose();
            });
          }
        }
      });
      this.scene.clear();
    }

    if (this.renderer) {
      this.renderer.dispose();
      this.renderer.forceContextLoss();
    }
  }
}